import type { ReelItem } from '~/components/PropertyReelsFeed/types';

import { useShortsNavigation } from '~/composables/shorts/navigation';
import { useFeedState } from '~/composables/shorts/useFeedState';
import { useLastVideoState } from '~/composables/shorts/useLastVideoState';
import {
	shortVideoToReelItem,
	useVideoHistory
} from '~/composables/shorts/useVideoHistory';
import { useShortsFeedStore } from '~/store/shortsFeedStore';
import { useVideoHistoryStore } from '~/store/videoHistoryStore';
import { waitUntilFalse } from '~/utils';

export function useShortsModal() {
	const feedStore = useShortsFeedStore();
	const historyStore = useVideoHistoryStore();
	const feedActions = useFeedState();
	const historyActions = useVideoHistory();
	const lastVideoState = useLastVideoState();
	const { navigateToShorts, navigateToVideo } = useShortsNavigation();

	const isOpen = useState('shortsModalOpen', () => false);
	const currentIndex = useState('shortsModalIndex', () => 0);
	const isFeedLoaded = useState('shortsModalFeedLoaded', () => false);

	const reelItems = computed(() => {
		const history = historyStore.historyItems;
		const feedList = feedStore.videos.map(shortVideoToReelItem);

		const historyIds = new Set(history.map(h => h.property.commercialId));
		const filteredFeed = feedList.filter(
			f => !historyIds.has(f.property.commercialId)
		);

		return [...history, ...filteredFeed] as ReelItem[];
	});

	const isReady = computed(() => {
		const historyReady =
			historyStore.isHistoryLoaded || historyStore.historyItems.length > 0;
		const feedReady = isFeedLoaded.value || feedStore.isPreloaded;
		return historyReady && feedReady;
	});

	const isLoading = computed(
		() => feedStore.isLoading || historyStore.isLoadingHistory
	);

	const historyCount = computed(() => historyStore.historyItems.length);

	async function loadAll(): Promise<void> {
		if (!import.meta.client) return;

		await Promise.all([historyActions.loadHistory(), loadFeed()]);
	}

	async function loadFeed(): Promise<void> {
		if (isFeedLoaded.value) return;

		if (feedStore.isLoading) {
			await waitUntilFalse(() => feedStore.isLoading);
			isFeedLoaded.value = true;
			return;
		}

		if (feedStore.isPreloaded) {
			isFeedLoaded.value = true;
			return;
		}

		await feedActions.fetchFeed();
		isFeedLoaded.value = true;
	}

	async function loadMoreHistory(): Promise<number> {
		const added = await historyActions.loadMoreHistory();
		if (added > 0) {
			currentIndex.value += added;
		}
		return added;
	}

	async function preload(): Promise<void> {
		if (feedStore.isPreloaded || feedStore.isLoading || !import.meta.client)
			return;
		await feedActions.fetchFeed();
		isFeedLoaded.value = true;
	}

	async function open(initialIndex?: number): Promise<void> {
		if (isLoading.value) return;

		await loadAll();

		if (initialIndex === undefined) {
			const restoredIndex = restoreLastPosition();
			currentIndex.value =
				restoredIndex !== null ? restoredIndex : historyCount.value;
		} else {
			currentIndex.value = initialIndex;
		}

		isOpen.value = true;
	}

	function close(): void {
		isOpen.value = false;
	}

	async function openByPropertyId(propertyId: string): Promise<void> {
		await loadAll();

		const index = reelItems.value.findIndex(
			v => v.property.commercialId === propertyId
		);

		const fallback = Math.min(historyCount.value, reelItems.value.length - 1);
		currentIndex.value = index >= 0 ? index : Math.max(0, fallback);
		isOpen.value = true;
	}

	function resetState(): void {
		currentIndex.value = 0;
		isFeedLoaded.value = false;
		historyActions.resetHistory();
		feedActions.reset();
	}

	function saveCurrentPosition(): void {
		const item = reelItems.value[currentIndex.value];
		if (item) {
			lastVideoState.save(item.property.commercialId, currentIndex.value);
		}
	}

	function restoreLastPosition(): number | null {
		const lastState = lastVideoState.get();
		if (!lastState) return null;

		const foundIndex = reelItems.value.findIndex(
			item => item.property.commercialId === lastState.propertyId
		);
		if (foundIndex >= 0) return foundIndex;

		if (lastState.index >= 0 && lastState.index < reelItems.value.length) {
			return lastState.index;
		}

		if (
			lastState.index >= reelItems.value.length &&
			reelItems.value.length > 0
		) {
			return reelItems.value.length - 1;
		}

		return null;
	}

	function getNewVideosStartIndex(): number {
		return historyCount.value;
	}

	return {
		isOpen,
		currentIndex,
		reelItems,
		isReady,
		isLoading,
		historyCount,

		open,
		openByPropertyId,
		close,
		loadAll,
		loadMoreFeed: feedActions.loadMore,
		loadMoreHistory,
		preload,
		resetState,
		applyFeedFilters: feedActions.applyFilters,
		navigateToShorts,
		navigateToVideo,
		saveCurrentPosition,
		restoreLastPosition,
		getNewVideosStartIndex
	};
}
